const HEN_ID_SELECTOR = '[data-hen-id]';

const INTERACTIVE_SELECTOR =
  'a, button, input, select, textarea, [role="button"], [role="link"]';

/** Nearest trackable host element for a DOM target. */
export function resolveHenIdHostFromTarget(
  target: EventTarget | null,
): Element | null {
  if (!(target instanceof Element)) {
    return null;
  }

  return target.closest(HEN_ID_SELECTOR);
}

export interface HenIdResolution {
  readonly host: Element;
  readonly componentId: string;
}

/** Resolves host + id in one `closest('[data-hen-id]')` walk. */
export function resolveHenIdFromTarget(
  target: EventTarget | null,
): HenIdResolution | null {
  const host = resolveHenIdHostFromTarget(target);
  if (!host) {
    return null;
  }

  const componentId = host.getAttribute('data-hen-id');
  if (!componentId || componentId.length === 0) {
    return null;
  }

  return { host, componentId };
}

/** Resolves the nearest passive `data-hen-id` from an event target. */
export function resolveComponentIdFromTarget(
  target: EventTarget | null,
): string | null {
  return resolveHenIdFromTarget(target)?.componentId ?? null;
}

/**
 * True when a click on an untracked control inside a mapped container should not
 * emit a separate interaction for the container's `data-hen-id`.
 */
export function isContainerAbsorbedInteraction(
  target: EventTarget | null,
  componentId: string,
): boolean {
  if (!(target instanceof Element)) {
    return false;
  }

  const host = resolveHenIdHostFromTarget(target);
  if (!host) {
    return false;
  }

  const hostComponentId = host.getAttribute('data-hen-id');
  if (hostComponentId !== componentId) {
    return false;
  }

  if (target === host) {
    return false;
  }

  const interactive = target.closest(INTERACTIVE_SELECTOR);
  if (!interactive || !host.contains(interactive) || interactive === host) {
    return false;
  }

  return !interactive.matches(HEN_ID_SELECTOR);
}

/** Collects trackable elements under an observation root. */
export function findHenIdElements(root: Document | Element): Element[] {
  const elements = Array.from(root.querySelectorAll(HEN_ID_SELECTOR));

  if (root instanceof Element && root.matches(HEN_ID_SELECTOR)) {
    return [root, ...elements];
  }

  return elements;
}
